import { Link } from 'react-router-dom';
import MainLayout from '@/components/layout/MainLayout';
import { Button } from '@/components/ui/button';
import { ArrowLeft } from 'lucide-react';

const PrivacyPolicy = () => {
  return (
    <MainLayout>
      <div className="container mx-auto px-4 py-16">
        <div className="max-w-4xl mx-auto">
          <div className="mb-8">
            <Link to="/" className="inline-flex items-center text-slate-400 hover:text-slate-200/80 mb-4">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Home
            </Link>
            <h1 className="text-4xl font-bold text-slate-200 mb-4">Privacy Policy</h1>
          </div>

          <div className="prose prose-lg max-w-none">
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-200 mb-4">1. Introduction</h2>
              <p className="text-slate-300 mb-4">
                Lobier ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our AI-powered hotel chatbot service.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-200 mb-4">2. Information We Collect</h2>
              <h3 className="text-xl font-medium text-slate-200 mb-3">2.1 Information You Provide</h3>
              <ul className="list-disc pl-6 text-slate-300 mb-4">
                <li>Hotel information and details you provide during registration</li>
                <li>Contact information (name, email, phone number)</li>
                <li>Hotel FAQs, services, and fact sheets you upload</li>
                <li>Messages and conversations with our AI chatbot</li>
              </ul>

              <h3 className="text-xl font-medium text-slate-200 mb-3">2.2 Automatically Collected Information</h3>
              <ul className="list-disc pl-6 text-slate-300 mb-4">
                <li>IP address and device information</li>
                <li>Browser type and version</li>
                <li>Usage data and interaction patterns</li>
                <li>Session information and cookies</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-200 mb-4">3. How We Use Your Information</h2>
              <p className="text-slate-300 mb-4">We use the collected information for the following purposes:</p>
              <ul className="list-disc pl-6 text-slate-300 mb-4">
                <li>To provide and maintain our AI chatbot service</li>
                <li>To personalize and improve the chatbot responses</li>
                <li>To communicate with you about our services</li>
                <li>To analyze usage patterns and improve our service</li>
                <li>To comply with legal obligations</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-200 mb-4">4. Information Sharing and Disclosure</h2>
              <p className="text-slate-300 mb-4">We do not sell, trade, or otherwise transfer your personal information to third parties, except in the following circumstances:</p>
              <ul className="list-disc pl-6 text-slate-300 mb-4">
                <li>With your explicit consent</li>
                <li>To comply with legal requirements</li>
                <li>To protect our rights and safety</li>
                <li>With service providers who assist in operating our service (under strict confidentiality agreements)</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-200 mb-4">5. Data Security</h2>
              <p className="text-slate-300 mb-4">
                We implement appropriate security measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. However, no method of transmission over the internet is 100% secure.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-200 mb-4">6. Data Retention</h2>
              <p className="text-slate-300 mb-4">
                We retain your personal information only for as long as necessary to fulfill the purposes outlined in this Privacy Policy, unless a longer retention period is required by law.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-200 mb-4">7. Your Rights</h2>
              <p className="text-slate-300 mb-4">You have the right to:</p>
              <ul className="list-disc pl-6 text-slate-300 mb-4">
                <li>Access your personal information</li>
                <li>Correct inaccurate information</li>
                <li>Request deletion of your information</li>
                <li>Object to processing of your information</li>
                <li>Withdraw consent at any time</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-200 mb-4">8. Cookies and Tracking</h2>
              <p className="text-slate-300 mb-4">
                We use cookies and similar tracking technologies to enhance your experience and analyze usage patterns. You can control cookie settings through your browser preferences.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-200 mb-4">9. Third-Party Services</h2>
              <p className="text-slate-300 mb-4">
                Our service may integrate with third-party services (such as AI providers). These services have their own privacy policies, and we encourage you to review them.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-200 mb-4">10. Children's Privacy</h2>
              <p className="text-slate-300 mb-4">
                Our service is not intended for children under 13 years of age. We do not knowingly collect personal information from children under 13.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-200 mb-4">11. International Data Transfers</h2>
              <p className="text-slate-300 mb-4">
                Your information may be transferred to and processed in countries other than your own. We ensure appropriate safeguards are in place for such transfers.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-200 mb-4">12. Changes to This Policy</h2>
              <p className="text-slate-300 mb-4">
                We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last updated" date.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-200 mb-4">13. Contact Us</h2>
              <p className="text-slate-300 mb-4">
                If you have any questions about this Privacy Policy, please contact us at:
              </p>
              <div className="bg-slate-800 p-4 rounded-lg">
                <p className="text-slate-300">
                  <strong>Email:</strong> info@lobier.com
                </p>
              </div>
            </section>
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default PrivacyPolicy; 